import { useState } from "react"
import { EngagementService } from "@services/engagements/engagementService"
import type { CampaignAudienceRow } from "@services/engagements/commands/cmdGetCampaignAudiences"
import type {
  CampaignStatus,
  CampaignTable,
  CampaignTableGroup,
  CampaignTableMetrics,
  CampaignTableRow,
} from "@services/engagements/commands/cmdGetCampaignTable"
import { formatStartDate } from "./dateFilter"

/*
    Monitor's landing view: every campaign in the engagement with its headline
    numbers, grouped by objective. Clicking a row opens that campaign's
    dashboard; clicking an objective heading opens the objective's.

    A campaign's audiences open underneath it in the table rather than only
    inside the dashboard, so "how did each audience do" is answerable without
    drilling in. They load on expand: each audience costs its own queries
    server-side, so the landing view doesn't pay for rows nobody opened.

    This replaced the engagement tree. It costs one request instead of the
    nineteen-dataset analytics payload, so the overview no longer pays for charts
    nobody has scrolled to.
*/

type Props = {
  engagementId: number
  table: CampaignTable
  onOpenCampaign: (launchId: number) => void
  onOpenObjective: (objectiveId: number) => void
  onOpenAudience: (launchId: number, audienceId: number) => void
}

export default function MonitorTable({
  engagementId,
  table,
  onOpenCampaign,
  onOpenObjective,
  onOpenAudience,
}: Props) {
  const groups = table.objectives

  if (!groups.length) {
    return (
      <div className="mon-empty">
        <div className="mon-empty__title">Nothing to monitor yet</div>
        <p className="mon-empty__body">
          This engagement has no campaigns. Create one in the Campaign step and its
          performance will appear here once it is running.
        </p>
      </div>
    )
  }

  // One unassigned group holding everything means the heading would label the
  // whole table — render the rows flat instead of wrapping them in a section
  // that says nothing.
  const isFlat = groups.length === 1 && groups[0].id === null

  return (
    <div className="mon-pane">
      <RollUp label="Engagement total" metrics={table.totals} />

      {isFlat ? (
        <div className="mon-card">
          <Header />
          {groups[0].campaigns.map((campaign) => (
            <CampaignRows
              key={campaign.id}
              engagementId={engagementId}
              campaign={campaign}
              onOpen={onOpenCampaign}
              onOpenAudience={onOpenAudience}
            />
          ))}
        </div>
      ) : (
        groups.map((group) => (
          <ObjectiveSection
            key={group.id ?? "unassigned"}
            engagementId={engagementId}
            group={group}
            onOpenCampaign={onOpenCampaign}
            onOpenObjective={onOpenObjective}
            onOpenAudience={onOpenAudience}
          />
        ))
      )}
    </div>
  )
}

function ObjectiveSection({
  engagementId,
  group,
  onOpenCampaign,
  onOpenObjective,
  onOpenAudience,
}: {
  engagementId: number
  group: CampaignTableGroup
  onOpenCampaign: (launchId: number) => void
  onOpenObjective: (objectiveId: number) => void
  onOpenAudience: (launchId: number, audienceId: number) => void
}) {
  return (
    <section className="mon-section">
      <div className="mon-section__head">
        {group.id == null ? (
          <span className="mon-section__title mon-section__title--muted">No objective</span>
        ) : (
          <button
            type="button"
            className="mon-section__title mon-section__title--link"
            onClick={() => onOpenObjective(group.id!)}
          >
            {group.name}
          </button>
        )}
        <span className="mon-section__count">
          {group.campaigns.length} {group.campaigns.length === 1 ? "campaign" : "campaigns"}
        </span>
        <span className="mon-section__totals">
          {money(group.totals.spend_total)} · {count(group.totals.impression_count)} impr ·{" "}
          {count(group.totals.total_conversions)} conv
        </span>
      </div>

      <Header />
      {group.campaigns.map((campaign) => (
        <CampaignRows
          key={campaign.id}
          engagementId={engagementId}
          campaign={campaign}
          onOpen={onOpenCampaign}
          onOpenAudience={onOpenAudience}
        />
      ))}
    </section>
  )
}

function RollUp({ label, metrics }: { label: string; metrics: CampaignTableMetrics }) {
  return (
    <div className="mon-rollup">
      <span className="mon-rollup__label">{label}</span>
      <Stat name="Spend" value={money(metrics.spend_total)} />
      <Stat name="Impressions" value={count(metrics.impression_count)} />
      <Stat name="Clicks" value={count(metrics.clicks_total)} />
      <Stat name="Conversions" value={count(metrics.total_conversions)} />
    </div>
  )
}

function Stat({ name, value }: { name: string; value: string }) {
  return (
    <div className="mon-rollup__stat">
      <span className="mon-rollup__key">{name}</span>
      <span className="mon-rollup__value">{value}</span>
    </div>
  )
}

function Header() {
  return (
    <div className="mon-row mon-row--head">
      {/* Lines the labels up with the rows, which lead with the expand caret. */}
      <span className="mon-row__caret-spacer" />
      <span className="mon-row__name">Campaign</span>
      <span className="mon-row__date">Campaign Start Date</span>
      <span className="mon-row__status">Status</span>
      <span className="mon-row__metric">Spend</span>
      <span className="mon-row__metric">Impressions</span>
      <span className="mon-row__metric">Clicks</span>
      <span className="mon-row__metric">Conversions</span>
      <span className="mon-row__chev" />
    </div>
  )
}

/*
    Where a campaign is in its life (MPN-1578): Onboarding before it has published, Live
    while it is running, Ended once its flight is over or every network link is archived.

    The server decides -- it is the side that can see the schedule and the network links,
    and having the browser re-derive it is how the badge and the header start disagreeing.
    An unrecognised value renders as nothing rather than as a wrong word: a future fourth
    state should show up as a gap someone reports, not as "Ended" on a live campaign.
*/
const STATUS_LABELS: Record<CampaignStatus, string> = {
  ONBOARDING: "Onboarding",
  LIVE: "Live",
  ENDED: "Ended",
}

function StatusPill({ status }: { status: CampaignStatus | null }) {
  if (!status || !(status in STATUS_LABELS)) return null
  return (
    <span className={`mon-status mon-status--${status.toLowerCase()}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

/*
    A campaign and, once expanded, its audiences. The caret is its own control
    beside the row: the row opens the dashboard, the caret opens the audiences,
    and one click never has to mean both.
*/
function CampaignRows({
  engagementId,
  campaign,
  onOpen,
  onOpenAudience,
}: {
  engagementId: number
  campaign: CampaignTableRow
  onOpen: (launchId: number) => void
  onOpenAudience: (launchId: number, audienceId: number) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [audiences, setAudiences] = useState<CampaignAudienceRow[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const load = async () => {
    setIsLoading(true)
    setError(false)
    try {
      setAudiences(await new EngagementService().getCampaignAudiences(engagementId, campaign.id))
    } catch {
      setError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const toggle = () => {
    const next = !expanded
    setExpanded(next)
    // Fetched once and kept: collapsing and reopening is free.
    if (next && audiences === null && !isLoading) void load()
  }

  return (
    <>
      <div className="mon-row mon-row--campaign">
        <button
          type="button"
          className={`mon-row__caret${expanded ? " mon-row__caret--open" : ""}`}
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={expanded ? "Hide audiences" : "Show audiences"}
        >
          <i className="bi bi-chevron-right" />
        </button>
        <button type="button" className="mon-row__open" onClick={() => onOpen(campaign.id)}>
          <span className="mon-row__name">
            {campaign.name}
            {!campaign.has_population_segment && (
              // Conversions are scoped by population segment, so a campaign without
              // one reads 0. Say so, or the zero looks like underperformance.
              <span className="mon-row__note">no population segment</span>
            )}
          </span>
          <span className="mon-row__date">{formatStartDate(campaign.go_live_date)}</span>
          <span className="mon-row__status">
            <StatusPill status={campaign.status} />
          </span>
          <span className="mon-row__metric">{money(campaign.spend_total)}</span>
          <span className="mon-row__metric">{count(campaign.impression_count)}</span>
          <span className="mon-row__metric">{count(campaign.clicks_total)}</span>
          <span className="mon-row__metric">{count(campaign.total_conversions)}</span>
          <span className="mon-row__chev">
            <i className="bi bi-chevron-right" />
          </span>
        </button>
      </div>

      {expanded && (
        <>
          {isLoading && <div className="mon-sub mon-sub--note">Loading audiences…</div>}

          {error && (
            <div className="mon-sub mon-sub--note">
              Could not load audiences.{" "}
              <button type="button" className="mon-sub__retry" onClick={load}>
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && audiences?.length === 0 && (
            <div className="mon-sub mon-sub--note">
              No audiences on this campaign — its ad groups have none assigned.
            </div>
          )}

          {audiences?.map((audience) => (
            <button
              key={audience.id}
              type="button"
              className="mon-sub"
              onClick={() => onOpenAudience(campaign.id, audience.id)}
            >
              <span className="mon-row__name mon-sub__name">{audience.name}</span>
              {/*
                  Spacers for the campaign row's start-date and status columns
                  (MPN-1578). Neither belongs to an audience -- a campaign starts and
                  ends as a whole -- but without them every number on this row sits a
                  column left of the campaign's and the table stops lining up.
              */}
              <span className="mon-row__date" aria-hidden="true" />
              <span className="mon-row__status" aria-hidden="true" />
              <span className="mon-row__metric">{money(audience.spend_total)}</span>
              <span className="mon-row__metric">{count(audience.impression_count)}</span>
              <span className="mon-row__metric">{count(audience.clicks_total)}</span>
              {/*
                  Conversions are scoped by population segment, which belongs to
                  the campaign — there is no honest per-audience split, and
                  repeating the campaign's number on each row would read as
                  several times the conversions it actually had.
              */}
              <span
                className="mon-row__metric mon-sub__na"
                title="Conversions are counted per population segment, which belongs to the campaign, so they can't be split by audience"
              >
                —
              </span>
              <span className="mon-row__chev">
                <i className="bi bi-chevron-right" />
              </span>
            </button>
          ))}
        </>
      )}
    </>
  )
}

const money = (value: number) =>
  value === 0 ? "—" : `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`

const count = (value: number) => (value === 0 ? "0" : value.toLocaleString())
